/**
 * Unified user identifier model for users/permissions API calls.
 *
 * The backend is standardizing on `user_email` (+ `created_at_ts` for
 * disambiguation) instead of numeric `user_id`. Service functions use these
 * helpers at the network boundary so Redux/components can keep legacy field
 * names during the rollover.
 */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * @param {string|number|null|undefined} value
 * @returns {boolean}
 */
const looksLikeEmail = (value) => {
    if (value == null || value === '') {
        return false;
    }

    return EMAIL_PATTERN.test(String(value));
};

/**
 * Resolve the canonical email from a raw user record or mutation params.
 *
 * @param {Object|null|undefined} raw
 * @returns {string}
 */
export const resolveUserEmail = (raw) => {
    if (!raw || typeof raw !== 'object') {
        return '';
    }

    if (raw.user_email) {
        return String(raw.user_email);
    }

    if (raw.email) {
        return String(raw.email);
    }

    if (looksLikeEmail(raw.user_id)) {
        return String(raw.user_id);
    }

    if (looksLikeEmail(raw.userID)) {
        return String(raw.userID);
    }

    return '';
};

/**
 * Resolve the legacy in-app `user_id` field from a raw user record.
 * During rollover this mirrors `user_email` when the backend stops sending ids.
 *
 * @param {Object|null|undefined} raw
 * @returns {string|number}
 */
export const resolveLegacyUserId = (raw) => {
    if (!raw || typeof raw !== 'object') {
        return '';
    }

    const email = resolveUserEmail(raw);
    if (email) {
        return email;
    }

    return raw.user_id ?? raw.id ?? '';
};

/**
 * Build a mutation body that sends `user_email` (+ optional `created_at_ts`)
 * instead of legacy `user_id` / `userID` path or body fields.
 *
 * @param {Object} [params={}]
 * @returns {Object}
 */
export const buildUserMutationBody = (params = {}) => {
    const {
        user_id: legacyUserId,
        userID,
        user_email,
        email,
        created_at_ts,
        ...rest
    } = params;

    const resolvedEmail = resolveUserEmail({
        user_email,
        email,
        user_id: legacyUserId,
        userID,
    });

    const body = { ...rest };

    if (resolvedEmail) {
        body.user_email = resolvedEmail;
    }

    if (created_at_ts != null && created_at_ts !== '') {
        body.created_at_ts = created_at_ts;
    }

    return body;
};
